'use client';

import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from 'react';
import {
  BloodVisionPlan,
  BloodVisionAddOn,
  DefaultDiscount,
  UIComponents,
} from './buy/types/bloodVision';
import { API_BASE_URL } from 'lib/config/apiEndpoints';
import { useSearchParams } from 'next/navigation';
import { useAffiliateMetadata, AffiliateDetails } from 'utils/affiliateHelpers';

export enum ModalityOptions {
  AT_HOME = 'at_home',
  WALK_IN = 'walk_in',
}

// Define the context state type with the new field

interface BloodVisionContextState {
  zipCode: string;
  hasUserProvidedZip: boolean;
  isZipValid: boolean; // Keep this for UI elements/components
  isLoading: boolean;
  plans: BloodVisionPlan[];
  selectedPlan: BloodVisionPlan | null;
  mainPlan: BloodVisionPlan | null; // Keep for backward compatibility
  pricePerMonth: number | null;
  ogPricePerMonth: number | null;
  addOns: BloodVisionAddOn[];
  selectedAddOns: number[];
  total: number | null;
  showStrikedPrice: boolean;
  isCountrySupported: boolean;
  availableModalityOptions: ModalityOptions[];
  selectedModality: ModalityOptions | null;
  isHsaFsaIncluded: boolean | null;
  setIsHsaFsaIncluded: (included: boolean | null) => void;
  setSelectedModality: (modality: ModalityOptions | null) => void;
  setAvailableModalityOptions: (options: ModalityOptions[]) => void;
  setZipCode: (zipCode: string) => void;
  handleZipCodeSubmit: (zipCode?: string) => Promise<void>;
  toggleAddOn: (addOnId: number) => void;
  handleCheckout: () => Promise<void>;
  checkoutAllowed: boolean;
  isZipValidUS: boolean;
  setIsZipValidUS?: (isValid: boolean) => void;
  zipDisclaimer: {
    title: string;
    icon: string;
    background_color: string;
    description: string;
    bullet_points: string[];
  } | null;
  hasCheckedOut: boolean;
  setHasCheckedOut: (checked: boolean) => void;
  // Add discount related fields
  discount: string | null;
  showDiscount: boolean;
  // Add plan selection
  hasUserSelectedPlan: boolean;
  selectPlanByUser: (plan: BloodVisionPlan) => void;
  setSelectedPlan: (plan: BloodVisionPlan | null) => void;
  // Add affiliate support
  affiliateDetails: AffiliateDetails | null;
  effectiveDiscount: { type: string; amount: number };
  campaignActive: boolean;
  // Add default discount support
  defaultDiscount: DefaultDiscount | null;
  // API-driven UI section visibility
  uiComponents: UIComponents;
  selectionRequiredHint: 'modality' | 'hsa' | null;
  showSelectionRequiredHint: (step: 'modality' | 'hsa') => void;
}

// Create the context with a default undefined value
const BloodVisionContext = createContext<BloodVisionContextState | undefined>(
  undefined,
);

// Provider props type
interface BloodVisionProviderProps {
  children: ReactNode;
  // Country is required - only 'us' is currently supported
  country: string;
}

// Countries supported by the US Blood Vision flow (IN has its own BloodVisionIndiaContext)
const BLOOD_VISION_SUPPORTED_COUNTRIES = new Set(['us']);

const DEFAULT_UI_COMPONENTS: UIComponents = {
  offer_section: true,
  zip_section: true,
  testing_location_section: true,
  hsa_fsa_section: true,
};

export function resolvePlanAfterModalityChange(
  previousSelection: BloodVisionPlan,
  previousPlans: BloodVisionPlan[],
  newPlans: BloodVisionPlan[],
): BloodVisionPlan | null {
  const byId = newPlans.find((p) => p.id === previousSelection.id);
  if (byId) return byId;

  const byPlanId = newPlans.find(
    (p) => p.plan_id === previousSelection.plan_id,
  );
  if (byPlanId) return byPlanId;

  const byName = newPlans.find((p) => p.name === previousSelection.name);
  if (byName) return byName;

  const previousIndex = previousPlans.findIndex(
    (p) => p.id === previousSelection.id,
  );
  if (previousIndex >= 0 && previousIndex < newPlans.length) {
    return newPlans[previousIndex];
  }

  return null;
}

// Provider component
export const BloodVisionProvider: React.FC<BloodVisionProviderProps> = ({
  children,
  country,
}) => {
  // const BV_STRIKED_PRICE = 499;
  const isCountrySupported = BLOOD_VISION_SUPPORTED_COUNTRIES.has(
    country.toLowerCase(),
  );
  const [zipCode, setZipCode] = useState<string>('');
  const [hasUserProvidedZip, setHasUserProvidedZip] = useState<boolean>(false);
  const [isZipValid, setIsZipValid] = useState<boolean>(false); // Keep for UI purposes
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [plans, setPlans] = useState<BloodVisionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<BloodVisionPlan | null>(
    null,
  );
  const [hasUserSelectedPlan, setHasUserSelectedPlan] = useState(false);
  const [mainPlan, setMainPlan] = useState<BloodVisionPlan | null>(null); // Keep for backward compatibility
  const [addOns, setAddOns] = useState<BloodVisionAddOn[]>([]);
  const [selectedAddOns, setSelectedAddOns] = useState<number[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState<boolean>(false);
  const [checkoutAllowed, setCheckoutAllowed] = useState<boolean>(false);
  const [showStrikedPrice, setShowStrikedPrice] = useState<boolean>(false);
  const [availableModalityOptions, setAvailableModalityOptions] = useState<
    ModalityOptions[]
  >([]);
  const [selectedModality, setSelectedModality] =
    useState<ModalityOptions | null>(null);
  const [isHsaFsaIncluded, setIsHsaFsaIncluded] = useState<boolean | null>(
    null,
  );
  const [selectionRequiredHint, setSelectionRequiredHint] = useState<
    'modality' | 'hsa' | null
  >(null);
  const [pricePerMonth, setPricePerMonth] = useState<number | null>(null);
  const [ogPricePerMonth, setOgPricePerMonth] = useState<number | null>(null);

  interface ZipDisclaimer {
    title: string;
    icon: string;
    background_color: string;
    description: string;
    bullet_points: string[];
  }

  const [zipDisclaimer, setZipDisclaimer] = useState<ZipDisclaimer | null>(
    null,
  );

  const [isZipValidUS, setIsZipValidUS] = useState<boolean>(true);
  const [hasCheckedOut, setHasCheckedOut] = useState<boolean>(false);

  const searchParams = useSearchParams();

  // Add discount logic
  const discount = searchParams?.get('discount') || null;
  const affiliateCode = searchParams?.get('affiliateCode') || null;

  // Show discount when both affiliateCode=birthday and discount are present
  const showDiscount = !!(affiliateCode === 'birthday' && discount);

  // Add affiliate support
  const { affiliateDetails } = useAffiliateMetadata(affiliateCode || '');
  const [campaignActive] = useState(false);

  // Add default discount state
  const [defaultDiscount, setDefaultDiscount] =
    useState<DefaultDiscount | null>(null);

  // API-driven UI section visibility
  const [uiComponents, setUiComponents] = useState<UIComponents>(
    DEFAULT_UI_COMPONENTS,
  );

  // Backend provides discount information in the plan object
  const effectiveDiscount = React.useMemo(() => {
    if (!selectedPlan) return { type: 'none', amount: 0 };

    // Check if there's a discount applied
    if (
      selectedPlan.price_split?.discount &&
      selectedPlan.price_split.discount > 0
    ) {
      // Determine discount type based on URL parameters and default discount
      if (affiliateCode === 'birthday' && discount) {
        return { type: 'birthday', amount: selectedPlan.price_split.discount };
      } else if (affiliateCode && affiliateCode !== 'birthday') {
        return { type: 'affiliate', amount: selectedPlan.price_split.discount };
      } else if (
        defaultDiscount &&
        defaultDiscount.active &&
        defaultDiscount.type === 'black_friday'
      ) {
        return {
          type: 'black_friday',
          amount: selectedPlan.price_split.discount,
        };
      }
    }

    return { type: 'none', amount: 0 };
  }, [selectedPlan, affiliateCode, discount, defaultDiscount]);

  // Reset HSA/FSA selection when modality changes
  useEffect(() => {
    setIsHsaFsaIncluded(null);
  }, [selectedModality]);

  useEffect(() => {
    if (selectedModality !== null) {
      setSelectionRequiredHint((hint) => (hint === 'modality' ? null : hint));
    }
  }, [selectedModality]);

  useEffect(() => {
    if (isHsaFsaIncluded !== null) {
      setSelectionRequiredHint((hint) => (hint === 'hsa' ? null : hint));
    }
  }, [isHsaFsaIncluded]);

  const showSelectionRequiredHint = (step: 'modality' | 'hsa') => {
    setSelectionRequiredHint(step);
  };

  // Update mainPlan when selectedPlan changes (for backward compatibility)
  useEffect(() => {
    setMainPlan(selectedPlan);
  }, [selectedPlan]);

  const clearSelectedPlan = () => {
    setSelectedPlan(null);
    setHasUserSelectedPlan(false);
  };

  const selectPlanByUser = (plan: BloodVisionPlan) => {
    setSelectedPlan(plan);
    setHasUserSelectedPlan(true);
  };

  // Handle ZIP code validation and fetch packages
  const handleZipCodeSubmit = async (input?: string) => {
    const isInitialLoad = !input && !zipCode;

    // Only proceed if we're in a supported country
    if (!isCountrySupported) {
      setIsZipValid(false);
      return;
    }

    const zipCodeToSend = input || zipCode;

    // Proceed with the API call
    setIsLoading(true);

    try {
      const urlToFetch = new URL(`${API_BASE_URL}/api/web_v1/package_list_v2/`);
      if (zipCodeToSend) {
        urlToFetch.searchParams.append('zip_code', zipCodeToSend);
      }
      // Always include walk_in as default modality for initial load
      urlToFetch.searchParams.append('modality', 'walk_in');

      if (affiliateCode) {
        urlToFetch.searchParams.append('affiliateCode', affiliateCode);
      }

      // Add discount if present
      if (discount) {
        urlToFetch.searchParams.append('discount', discount);
      }

      const response = await fetch(urlToFetch.href, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.status === 'ok' && data.data?.plans) {
        setPlans(data.data.plans);
        setAddOns(data.data.add_ons || []);
        setIsZipValid(true); // Set to true to show UI elements

        if (
          'additional_info' in data.data &&
          'disclaimer' in data.data.additional_info
        ) {
          setZipDisclaimer(data.data.additional_info.disclaimer);
        } else setZipDisclaimer(null);

        // Set default discount if present
        if (data.data.default_discount) {
          setDefaultDiscount(data.data.default_discount);
        } else {
          setDefaultDiscount(null);
        }

        // Set UI component visibility
        setUiComponents({
          ...DEFAULT_UI_COMPONENTS,
          ...(data.data.ui_components || {}),
        });

        if (!isInitialLoad) {
          // If this is from a user submission (not initial load)
          setZipCode(zipCodeToSend);
          setHasUserProvidedZip(true);

          if (data.data.plans.length > 0) {
            selectPlanByUser(data.data.plans[0]);
          }
        } else {
          setHasUserProvidedZip(false);
        }
      } else {
        // No packages available for this ZIP code
        if (!isInitialLoad) {
          // Only reset these if it's not the initial load
          setIsZipValid(false);
          setPlans([]);
          clearSelectedPlan();
          setAddOns([]);
          setHasUserProvidedZip(false);
        }
        setUiComponents(DEFAULT_UI_COMPONENTS);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      if (!isInitialLoad) {
        // Only reset these if it's not the initial load
        setIsZipValid(false);
        setPlans([]);
        clearSelectedPlan();
        setAddOns([]);
        setHasUserProvidedZip(false);
      }
      setUiComponents(DEFAULT_UI_COMPONENTS);
    } finally {
      setIsLoading(false);
    }
  };

  // New function to fetch packages when modality changes
  const fetchPackagesForModality = async (modality: ModalityOptions) => {
    if (!zipCode || !isZipValid || !hasUserProvidedZip) {
      return;
    }

    const previousPlans = plans;
    const previousSelection = selectedPlan;
    const hadUserSelection = hasUserSelectedPlan;

    setIsLoading(true);

    try {
      const urlToFetch = new URL(`${API_BASE_URL}/api/web_v1/package_list_v2/`);
      urlToFetch.searchParams.append('zip_code', zipCode);
      urlToFetch.searchParams.append('modality', modality);
      if (affiliateCode) {
        urlToFetch.searchParams.append('affiliateCode', affiliateCode);
      }

      // Add discount if present
      if (discount) {
        urlToFetch.searchParams.append('discount', discount);
      }

      const response = await fetch(urlToFetch.href, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.status === 'ok' && data.data?.plans) {
        const newPlans = data.data.plans;
        const newAddOns = data.data.add_ons || [];

        setPlans(newPlans);
        setAddOns(newAddOns);
        setIsZipValid(true);

        const validAddOnIds = new Set(
          newAddOns.map((addon: BloodVisionAddOn) => addon.id),
        );
        setSelectedAddOns((prev) => prev.filter((id) => validAddOnIds.has(id)));

        if (hadUserSelection && previousSelection) {
          const matched = resolvePlanAfterModalityChange(
            previousSelection,
            previousPlans,
            newPlans,
          );
          if (matched) {
            setSelectedPlan(matched);
          } else {
            clearSelectedPlan();
          }
        } else if (newPlans.length > 0) {
          selectPlanByUser(newPlans[0]);
        }

        if (
          'additional_info' in data.data &&
          'disclaimer' in data.data.additional_info
        ) {
          setZipDisclaimer(data.data.additional_info.disclaimer);
        } else setZipDisclaimer(null);

        // Set default discount if present
        if (data.data.default_discount) {
          setDefaultDiscount(data.data.default_discount);
        } else {
          setDefaultDiscount(null);
        }

        // Set UI component visibility
        setUiComponents({
          ...DEFAULT_UI_COMPONENTS,
          ...(data.data.ui_components || {}),
        });
      } else {
        setIsZipValid(false);
        setPlans([]);
        clearSelectedPlan();
        setAddOns([]);
        setUiComponents(DEFAULT_UI_COMPONENTS);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      setIsZipValid(false);
      setPlans([]);
      clearSelectedPlan();
      setAddOns([]);
      setUiComponents(DEFAULT_UI_COMPONENTS);
    } finally {
      setIsLoading(false);
    }
  };

  // Update the existing setSelectedModality to only set the value
  const handleModalitySelection = (modality: ModalityOptions | null) => {
    setSelectedModality(modality);
  };

  // Toggle add-on selection
  const toggleAddOn = (addOnId: number) => {
    setSelectedAddOns((prev) => {
      if (prev.includes(addOnId)) {
        return prev.filter((id) => id !== addOnId);
      } else {
        return [...prev, addOnId];
      }
    });
  };

  // Handling checkout
  const handleCheckout = async () => {
    // For checkout, require a valid US ZIP (skip if zip section is hidden)
    if (uiComponents.zip_section && !isZipValid) {
      console.error('Cannot checkout with invalid US ZIP code:', zipCode);
      return;
    }

    if (!selectedPlan) {
      console.error('No plan selected');
      return;
    }

    if (!isCountrySupported) {
      console.error('Checkout only available in supported countries');
      return;
    }

    // if (isHsaFsaIncluded === null) {
    //   console.error('HSA/FSA selection is required');
    //   return;
    // }

    setIsLoading(true);

    try {
      const payload = {
        product_type: 'blood_vision',
        plan_id: [selectedPlan.id],
        add_on_ids: selectedAddOns,
        country: country.toLowerCase(),
        // Payment source is determined by HSA/FSA selection
        payment_source: isHsaFsaIncluded ? 'flex' : 'stripe',
        success_url:
          window.location.origin +
          `/order/blood-vision/?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: window.location.href,
        metadata: {
          ...Object.fromEntries(searchParams?.entries() ?? []),
          // Add discount code if Black Friday discount is active
          ...(defaultDiscount &&
          defaultDiscount.active &&
          defaultDiscount.type === 'black_friday'
            ? { affiliateCode: defaultDiscount.code }
            : {}),
        },
      };

      const response = await fetch(API_BASE_URL + '/api/v1/subscriptions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Platform: 'web',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'ok' && data.data?.checkout_url) {
        // Redirecting to Stripe checkout
        window.location.href = data.data.checkout_url;
      } else {
        throw new Error('No checkout URL returned or invalid response format');
      }
    } catch (error) {
      console.error('Error initiating checkout:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Check if current plan is a campaign plan
    const isCampaignPlan = selectedPlan?.is_plan_campaign === true;

    // Determine which fields are required based on ui_components visibility
    const zipRequired = uiComponents.zip_section;
    const modalityRequired = uiComponents.testing_location_section;
    const hsaFsaRequired = !isCampaignPlan && uiComponents.hsa_fsa_section;

    console.log('🎯 Checkout Validation:', {
      planName: selectedPlan?.name,
      is_plan_campaign: selectedPlan?.is_plan_campaign,
      isCampaignPlan,
      hsaFsaRequired,
      zipRequired,
      modalityRequired,
      zipCode,
      isZipValid,
      selectedModality,
      isHsaFsaIncluded,
    });

    // If any required field is missing, disable checkout
    if (
      (zipRequired &&
        (!hasUserProvidedZip ||
          !zipCode ||
          zipCode.length !== 5 ||
          !isZipValid ||
          !isZipValidUS)) ||
      (modalityRequired && selectedModality === null) ||
      (hsaFsaRequired && isHsaFsaIncluded === null)
    ) {
      setCheckoutAllowed(false);
      return;
    }
    // Enable checkout only when all required fields are filled
    if (
      (!zipRequired ||
        (hasUserProvidedZip && isZipValid && zipCode && isZipValidUS)) &&
      selectedPlan &&
      (!modalityRequired || selectedModality !== null) &&
      (!hsaFsaRequired || isHsaFsaIncluded !== null)
    ) {
      setCheckoutAllowed(true);
    }
  }, [
    zipCode,
    hasUserProvidedZip,
    isZipValid,
    selectedPlan,
    isZipValidUS,
    selectedModality,
    isHsaFsaIncluded,
    uiComponents,
  ]);

  // Updating total when selections change
  useEffect(() => {
    if (!selectedPlan) return;
    const plan = plans.find((p) => p.id === selectedPlan.id) ?? selectedPlan;
    const mainPlanPrice = plan.price;
    const isDiscounted = plan.discount_applied || showDiscount;
    let calculatedTotal = mainPlanPrice;

    if (isDiscounted) {
      setShowStrikedPrice(true);
    } else setShowStrikedPrice(false);

    selectedAddOns.forEach((addOnId) => {
      const addOn = addOns.find((addon) => addon.id === addOnId);
      if (addOn) {
        calculatedTotal += parseFloat(addOn.price.replace('$', ''));
      }
    });

    setTotal(calculatedTotal);
    setPricePerMonth(Number(plan.price) / 12);
    setOgPricePerMonth(Number(plan.original_price) / 12);
  }, [selectedPlan, selectedAddOns, addOns, plans, isZipValid, showDiscount]);

  // Waiting for search params to be available before making the initial API call
  useEffect(() => {
    // If params are available and the API call hasn't been made yet
    if (searchParams && !initialLoadDone) {
      // Calling handleZipCodeSubmit with no ZIP for initial load
      handleZipCodeSubmit();
      setInitialLoadDone(true);
    }
  }, [searchParams, initialLoadDone]);

  // Always fetch packages when selectedModality changes and is not null
  useEffect(() => {
    if (selectedModality) {
      fetchPackagesForModality(selectedModality);
    }
  }, [selectedModality]);

  // Debug context state whenever it changes
  // useEffect(() => {
  //   console.log('BloodVisionContext state updated:', {
  //     zipCode,
  //     isZipValid,
  //     hasMainPlan: !!mainPlan,
  //     addOnsCount: addOns.length,
  //     selectedAddOnsCount: selectedAddOns.length,
  //     isLoading,
  //   });
  // }, [zipCode, isZipValid, mainPlan, addOns, selectedAddOns, isLoading]);

  // Custom setZipCode that resets modality and HSA/FSA
  const setZipCodeAndReset = (zip: string) => {
    setZipCode(zip);
    setHasUserProvidedZip(zip.length === 5);
    setSelectedModality(null);
    setIsHsaFsaIncluded(null);
    setZipDisclaimer(null);
    clearSelectedPlan();
    setSelectionRequiredHint(null);
  };

  // Custom setAvailableModalityOptions with auto-selection logic
  const setAvailableModalityOptionsWithAutoSelect = (
    options: ModalityOptions[],
  ) => {
    setAvailableModalityOptions(options);
    // Only auto-select if user hasn't already selected
    if (!selectedModality) {
      if (options.includes(ModalityOptions.WALK_IN)) {
        setSelectedModality(ModalityOptions.WALK_IN);
      } else if (
        options.length === 1 &&
        options[0] === ModalityOptions.AT_HOME
      ) {
        setSelectedModality(ModalityOptions.AT_HOME);
      } else {
        setSelectedModality(null);
      }
    }
  };

  // Update the context value to use the new function
  const contextValue: BloodVisionContextState = {
    zipCode,
    hasUserProvidedZip,
    isZipValid,
    isLoading,
    plans,
    selectedPlan,
    mainPlan,
    addOns,
    selectedAddOns,
    total,
    isCountrySupported,
    availableModalityOptions,
    selectedModality,
    isHsaFsaIncluded,
    setIsHsaFsaIncluded,
    setSelectedModality: handleModalitySelection,
    setAvailableModalityOptions: setAvailableModalityOptionsWithAutoSelect,
    setZipCode: setZipCodeAndReset,
    handleZipCodeSubmit,
    toggleAddOn,
    handleCheckout,
    checkoutAllowed,
    isZipValidUS,
    setIsZipValidUS,
    showStrikedPrice,
    zipDisclaimer,
    hasCheckedOut,
    setHasCheckedOut,
    pricePerMonth,
    ogPricePerMonth,
    discount,
    showDiscount,
    hasUserSelectedPlan,
    selectPlanByUser,
    setSelectedPlan,
    // Add affiliate support
    affiliateDetails: affiliateDetails,
    effectiveDiscount: effectiveDiscount,
    campaignActive: campaignActive,
    // Add default discount support
    defaultDiscount: defaultDiscount,
    // API-driven UI section visibility
    uiComponents,
    selectionRequiredHint,
    showSelectionRequiredHint,
  };

  return (
    <BloodVisionContext.Provider value={contextValue}>
      {children}
    </BloodVisionContext.Provider>
  );
};

// Custom hook to use the BloodVision context
export const useBloodVision = (): BloodVisionContextState => {
  const context = useContext(BloodVisionContext);
  if (context === undefined) {
    throw new Error('useBloodVision must be used within a BloodVisionProvider');
  }
  return context;
};
